/**
 * 装柜节点作业页（F02）—— 对齐后台子步骤面板的简化版。
 *
 * 设计原则（2026-08-13 简化同步后台）：不再维护独立的柜选择 / IoT 绑定 / 封柜二次确认区块，
 * 全部交由 NodeSubstepPanel 处理（与后台 NodeSubstepPanel 同构）。
 * 节点级附件（node_doc_rules 中 substepCode 为空的规则）单独展示并随 advance.files 提交。
 * 实际发生时间由后端/服务自动记录，前端不再默认展示时间窗口。
 */
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getAdvancePermission, advanceOrder, completeOrder } from '@/api/fulfillment';
import { getNodeDocRules, type NodeDocRuleItem } from '@/api/substep';
import { useNodeConfig } from '@/utils/nodeConfig';
import NodeSubstepPanel from '@/components/mobile/NodeSubstepPanel';
import { MobileAppBar, NodeHero, InfoCard } from '@/components/mobile/ui';
import { T } from '@/theme/tokens';
import NodeDocUploader from '@/components/mobile/NodeDocUploader';
import CreateContainerDialog from '@/components/mobile/CreateContainerDialog';
import type { NodeActionPermissionVO, DocFilePair, NodeActionHintVO } from '@/api/types';

export default function LoadNodePage() {
  const { orderId = '' } = useParams();
  const location = useLocation();
  const passedState = (location.state as {
    nodeCode?: string;
    orderNo?: string;
  } | null);
  const navigate = useNavigate();
  const { getNodeName } = useNodeConfig();

  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState<NodeActionPermissionVO | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  // 建柜弹窗（装柜节点处理时弹出；领导要求建柜在装柜节点而非备货生产时）
  const [createContainerOpen, setCreateContainerOpen] = useState(false);
  const [createContainerHint, setCreateContainerHint] = useState<NodeActionHintVO | null>(null);
  const [needArchiveCreate, setNeedArchiveCreate] = useState(false);
  const [panelKey, setPanelKey] = useState(0);

  // 节点级附件规则与已上传文件（ advance.files 来源）
  const [nodeRules, setNodeRules] = useState<NodeDocRuleItem[]>([]);
  const [nodeFiles, setNodeFiles] = useState<DocFilePair[]>([]);

  // load 节点允许显式透传 nodeCode，否则固定为 load；同样支持 orderNo 显示
  const [effectiveNodeCode, setEffectiveNodeCode] = useState<string>(passedState?.nodeCode ?? 'load');
  const displayOrderNo = passedState?.orderNo || orderId;

  const loadPermission = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const perm = await getAdvancePermission(orderId);
      setPermission(perm);
      // 如果后端返回的当前节点不是 load，以实际权限为准（防御性修正）
      if (perm.currentNodeCode && perm.currentNodeCode !== effectiveNodeCode) {
        setEffectiveNodeCode(perm.currentNodeCode);
      }
    } catch (e: unknown) {
      setSnack({ msg: (e as { message?: string })?.message || '加载失败', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [orderId, effectiveNodeCode]);

  useEffect(() => {
    loadPermission();
  }, [loadPermission]);

  // 加载当前节点级附件规则（对齐后台 UpdateNodeDialog：展示当前节点全部 rules，不再过滤 substepCode）
  useEffect(() => {
    if (!effectiveNodeCode) return;
    let cancelled = false;
    getNodeDocRules(effectiveNodeCode, orderId)
      .then((rules) => {
        if (cancelled) return;
        setNodeRules(Array.isArray(rules) ? rules : []);
      })
      .catch((err) => {
        console.error('加载节点附件规则失败', err);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveNodeCode, orderId]);

  const allRequiredDocsUploaded = useMemo(
    () => nodeRules.filter((r) => r.required).every((r) => nodeFiles.some((f) => f.docType === r.docType)),
    [nodeRules, nodeFiles],
  );

  const handleAdvance = async () => {
    if (!permission || !orderId) return;
    // 装柜节点：尚未建立柜档案时，先弹出建柜窗，而非直接推进到出口报关
    if (effectiveNodeCode === 'load' && needArchiveCreate) {
      setCreateContainerHint(null);
      setCreateContainerOpen(true);
      return;
    }
    if (nodeRules.length > 0 && !allRequiredDocsUploaded) {
      setSnack({ msg: '请先完成节点必填附件上传', sev: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      if (permission.lastNode) {
        await completeOrder(orderId);
        setSnack({ msg: '订单已完成', sev: 'success' });
        setTimeout(() => navigate('/mobile/tasks', { replace: true }), 1200);
        return;
      }
      await advanceOrder(orderId, {
        expectedFromNodeCode: permission.currentNodeCode,
        trigger: 'MOBILE',
        files: nodeFiles,
      });
      setSnack({
        msg: permission.advanceMode === 'OBSERVE_CONFIRM' ? '已确认完成' : '已提交推进',
        sev: 'success',
      });
      const after = await getAdvancePermission(orderId);
      if (after.currentNodeCode !== permission.currentNodeCode) {
        setSnack({ msg: `已推进至 ${after.nextNodeCode ?? after.currentNodeCode}`, sev: 'success' });
        setTimeout(() => navigate('/mobile/tasks', { replace: true }), 1200);
      } else {
        setPermission(after);
      }
    } catch (e: unknown) {
      setSnack({ msg: (e as { message?: string })?.message || '推进失败', sev: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isObserve = permission?.advanceMode === 'OBSERVE_CONFIRM';
  const isReadOnly = permission != null && !permission.canAdvance;
  const btnLabel = isObserve ? '确认完成' : '提交推进';
  const btnVariant = isObserve ? 'outlined' : 'contained';

  return (
    <Box sx={{ pb: 2 }}>
      <MobileAppBar title="装柜作业" onBack={() => navigate('/mobile/tasks', { replace: true })} />

      <Box sx={{ px: 2, pt: 2 }}>
        <NodeHero
          orderNo={displayOrderNo}
          nodeName={getNodeName(permission?.currentNodeCode)}
          lastNode={permission?.lastNode}
        />

        {permission?.observeNode && (
          <InfoCard>
            本节点为观察节点，录入船期后将自动推进
            {permission.autoAdvanceEnabled ? `（${permission.autoSourceLabel}）` : ''}，也可手动确认完成。
            {permission.lastNode && '本节点为订单最终节点，确认完成后订单即完结。'}
          </InfoCard>
        )}

        {isReadOnly && (
          <InfoCard>
            当前角色对本节点为只读（抄送），无法操作。
            {permission?.ownerRoleNames?.length
              ? `本节点由【${permission.ownerRoleNames.join('、')}】负责。`
              : '请由节点责任人处理。'}
          </InfoCard>
        )}

        {/* 节点级附件上传（对齐后台 UpdateNodeDialog） */}
        {!isReadOnly && effectiveNodeCode && (
          <NodeDocUploader
            orderId={orderId}
            nodeCode={effectiveNodeCode}
            rules={nodeRules}
            onChange={setNodeFiles}
          />
        )}

        {/* 子步骤面板（对齐后台 NodeSubstepPanel；load 节点内含建柜入口） */}
        {!isReadOnly && effectiveNodeCode && (
          <Box sx={{ mt: 2 }}>
            <NodeSubstepPanel
              key={panelKey}
              orderId={orderId}
              nodeCode={effectiveNodeCode}
              onComplete={() => loadPermission()}
              onArchiveHintChange={(hint) => setNeedArchiveCreate(Boolean(hint?.needArchiveCreate))}
              onRequestNewArchive={(hint) => {
                setCreateContainerHint(hint);
                setCreateContainerOpen(true);
              }}
            />
          </Box>
        )}

        {/* 推进按钮 */}
        {!isReadOnly && (
          <Button
            fullWidth
            variant={btnVariant}
            disabled={!permission?.canAdvance || submitting}
            onClick={handleAdvance}
            sx={{ mt: 2.5, height: 50, fontSize: '16px', fontWeight: 700, letterSpacing: '.04em', borderRadius: '14px' }}
          >
            {submitting ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : btnLabel}
          </Button>
        )}
        {isReadOnly && (
          <Typography sx={{ textAlign: 'center', fontSize: '12px', color: T.ink3, mt: 2.25, mb: 1, lineHeight: 1.6 }}>
            该节点由责任人操作，此处仅可查看进度
          </Typography>
        )}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack(null)}
      >
        <Alert severity={snack?.sev} sx={{ fontSize: '13px' }}>
          {snack?.msg}
        </Alert>
      </Snackbar>

      {/* 建立柜档案弹窗（装柜节点处理时弹出，替代原备货生产阶段的强制建柜） */}
      <CreateContainerDialog
        open={createContainerOpen}
        orderId={orderId}
        prefillContainerNo={createContainerHint?.prefillContainerNo}
        onClose={() => setCreateContainerOpen(false)}
        onCreated={async () => {
          setCreateContainerOpen(false);
          setNeedArchiveCreate(false);
          setPanelKey((k) => k + 1);
          setSnack({ msg: '柜档案已创建，可继续装柜作业', sev: 'success' });
          await loadPermission();
        }}
      />
    </Box>
  );
}
